import React, { useCallback, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { Button, Typography, Modal, Box } from "@mui/material";
import { DateCalendar, DateView } from "@mui/x-date-pickers";
import { format, getYear, startOfDay } from "date-fns";
import { theme } from "uikit";

import { StyledColumn, StyledRow } from "../../styles";
import { CustomDatePickerLocale } from "../interface";

const StyledRoot = styled(StyledColumn)`
	width: fit-content;
	background-color: white;
	box-shadow: rgba(0, 0, 0, 0.3) 0px 0px 8px 2px;
	border-radius: 5px;
	overflow: hidden;
`;

const StyledDate = styled(Typography)`
	background-color: ${theme.colors.primary};
	color: white;
	font-size: 32px;
	font-weight: 400;
	padding: 16px 24px;

	.year {
		font-size: 16px;
	}
`;

const StyledDateCalendar = styled(DateCalendar)`
	& .Mui-selected {
		background-color: ${theme.colors.accent} !important;
	}
` as typeof DateCalendar;

const CalendarModal: React.FC<CalendarModal.Props> = ({
	value,
	onSubmit,
	onCancel,
	minDate,
	maxDate,
	locale,
	open,
	onClose,
}) => {
	const [date, setDate] = useState(startOfDay(value ?? new Date()));
	const [view, setView] = useState<DateView>("day");

	const minDateNormalized = useMemo(
		() => (minDate ? startOfDay(minDate) : undefined),
		[minDate],
	);
	const maxDateNormalized = useMemo(
		() => (maxDate ? startOfDay(maxDate) : undefined),
		[maxDate],
	);

	const today = useCallback(() => {
		const now = startOfDay(new Date());
		setDate(now);
	}, []);

	const confirm = useCallback(() => onSubmit(date), [onSubmit, date]);

	return (
		<Modal open={open} onClose={onClose} disablePortal>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					outline: "none",
				}}
			>
				<StyledRoot alignItems="stretch">
					<StyledDate>
						<div className="year">{getYear(date)}</div>
						{format(date, "eee, d MMM", {
							locale: locale?.dateFnsLocale,
						})}
					</StyledDate>

					<StyledDateCalendar
						value={date}
						onChange={(newValue) => {
							if (newValue) {
								setDate(newValue);
							}
						}}
						views={["year", "month", "day"]}
						view={view}
						onViewChange={setView}
						showDaysOutsideCurrentMonth
						maxDate={maxDateNormalized}
						minDate={minDateNormalized}
					/>

					<StyledRow
						justify="space-between"
						alignItems="center"
						style={{ paddingInline: 16, paddingBlock: 8 }}
					>
						<Button color="inherit" variant="text" onClick={today}>
							{locale?.todayButton ?? "today"}
						</Button>

						<StyledRow>
							<Button
								color="inherit"
								variant="text"
								onClick={onCancel}
							>
								{locale?.cancelButton ?? "cancel"}
							</Button>
							<Button
								color="inherit"
								variant="text"
								onClick={confirm}
							>
								{locale?.submitButton ?? "ok"}
							</Button>
						</StyledRow>
					</StyledRow>
				</StyledRoot>
			</Box>
		</Modal>
	);
};

declare namespace CalendarModal {
	interface Props {
		value: Date | null;
		onSubmit: (value: Date) => void;
		onCancel: () => void;
		minDate?: Date;
		maxDate?: Date;
		locale?: CustomDatePickerLocale;
		open: boolean;
		onClose: () => void;
	}
}

export default CalendarModal;
